<!-- src/components/portafolio/SpaZen.vue -->
<template>
  <div class="spa-zen">
    <!-- Botón de regreso -->
    <v-btn
      absolute
      top
      left
      fab
      small
      color="#6B8E6B"
      dark
      class="ma-4"
      @click="$router.push('/portafolio')"
    >
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>

    <!-- Hero Section con video de fondo -->
    <v-parallax
      src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874"
      height="600"
    >
      <v-container fill-height>
        <v-row align="center" justify="center" class="text-center">
          <v-col cols="12" md-8>
            <div class="hero-content">
              <v-icon size="80" color="white" class="mb-4">mdi-flower-lotus</v-icon>
              <h1 class="display-3 font-weight-bold white--text mb-4">Spa Zen</h1>
              <h2 class="headline white--text mb-6 font-weight-light">
                Encuentra tu equilibrio interior
              </h2>
              <v-btn
                color="#6B8E6B"
                dark
                x-large
                rounded
                elevation-10
                @click="scrollToSection('reservas')"
              >
                <v-icon left>mdi-calendar</v-icon>
                Reserva tu experiencia
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-parallax>

    <!-- Mensaje de bienvenida -->
    <v-container class="mt-12">
      <v-row justify="center">
        <v-col cols="12" md-8 class="text-center">
          <h2 class="display-1 font-weight-light mb-4">Bienvenido a Spa Zen</h2>
          <v-divider class="my-4" style="width: 100px; margin: 0 auto"></v-divider>
          <p class="subtitle-1 grey--text text--darken-1 mt-4">
            Un oasis de tranquilidad donde el estrés desaparece y la armonía renace.
            Ofrecemos tratamientos holísticos que nutren cuerpo, mente y espíritu.
          </p>
        </v-col>
      </v-row>
    </v-container>

    <!-- Servicios destacados -->
    <v-container class="mt-12">
      <v-row justify="center">
        <v-col cols="12" class="text-center">
          <h2 class="display-1 font-weight-light mb-2">Nuestros Servicios</h2>
          <p class="subtitle-1 grey--text">Descubre la experiencia Zen</p>
        </v-col>
      </v-row>

      <v-row class="mt-8">
        <v-col v-for="(service, idx) in services" :key="idx" cols="12" sm="6" md="4">
          <v-hover v-slot="{ hover }">
            <v-card
              :elevation="hover ? 16 : 4"
              class="service-card"
              :class="{ 'service-card-hover': hover }"
              @click="selectService(service)"
            >
              <v-img :src="service.image" height="250" class="service-image">
                <div class="service-overlay" v-if="hover">
                  <v-icon size="48" color="white">mdi-plus-circle</v-icon>
                </div>
              </v-img>

              <v-card-title class="justify-center pt-4">
                {{ service.name }}
              </v-card-title>

              <v-card-text class="text-center">
                <p class="body-2 grey--text">{{ service.description }}</p>
                <div class="d-flex justify-center align-center mt-2">
                  <v-rating
                    v-model="service.rating"
                    readonly
                    dense
                    half-increments
                    color="#FFD700"
                    size="20"
                  ></v-rating>
                  <span class="ml-2 caption grey--text">({{ service.reviews }})</span>
                </div>
                <div class="mt-3">
                  <span class="display-1 font-weight-bold" :style="{ color: '#6B8E6B' }">
                    S/ {{ service.price }}
                  </span>
                  <span class="caption grey--text"> por sesión</span>
                </div>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  block
                  color="#6B8E6B"
                  dark
                  rounded
                  @click.stop="selectService(service)"
                >
                  Reservar ahora
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>
    </v-container>

    <!-- Galería de imágenes -->
    <v-container class="mt-12">
      <v-row justify="center">
        <v-col cols="12" class="text-center">
          <h2 class="display-1 font-weight-light mb-2">Nuestro Espacio</h2>
          <p class="subtitle-1 grey--text">Un ambiente diseñado para tu bienestar</p>
        </v-col>
      </v-row>

      <v-row class="mt-8">
        <v-col v-for="(img, idx) in gallery" :key="idx" cols="12" sm="6" md="3">
          <v-img :src="img" height="200" class="gallery-img" @click="openGallery(idx)">
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="#6B8E6B"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </v-col>
      </v-row>
    </v-container>

    <!-- Beneficios -->
    <v-container class="mt-12">
      <v-row>
        <v-col cols="12" class="text-center">
          <h2 class="display-1 font-weight-light mb-2">¿Por qué elegirnos?</h2>
        </v-col>
      </v-row>

      <v-row class="mt-8">
        <v-col v-for="(benefit, idx) in benefits" :key="idx" cols="12" md="4">
          <v-card flat class="text-center benefit-card">
            <v-icon size="64" :color="benefit.color">{{ benefit.icon }}</v-icon>
            <h3 class="title mt-3">{{ benefit.title }}</h3>
            <p class="body-2 grey--text mt-2">{{ benefit.description }}</p>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Testimonios -->
    <v-container class="mt-12">
      <v-row justify="center">
        <v-col cols="12" class="text-center">
          <h2 class="display-1 font-weight-light mb-2">Testimonios</h2>
          <p class="subtitle-1 grey--text">Lo que dicen nuestros clientes</p>
        </v-col>
      </v-row>

      <v-row class="mt-8">
        <v-col v-for="(testimonial, idx) in testimonials" :key="idx" cols="12" md="4">
          <v-card class="testimonial-card" elevation="3">
            <v-card-text>
              <v-icon color="#6B8E6B" size="32" class="mb-2"
                >mdi-format-quote-open</v-icon
              >
              <p class="body-1 font-italic">"{{ testimonial.text }}"</p>
              <div class="d-flex align-center mt-4">
                <v-avatar size="50">
                  <v-img :src="testimonial.avatar"></v-img>
                </v-avatar>
                <div class="ml-3">
                  <p class="mb-0 font-weight-bold">{{ testimonial.name }}</p>
                  <v-rating
                    :value="testimonial.rating"
                    readonly
                    dense
                    small
                    color="#FFD700"
                  ></v-rating>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Formulario de reserva -->
    <v-container id="reservas" class="mt-12">
      <v-card color="#F5F5DC" class="pa-8">
        <v-row>
          <v-col cols="12" md="6">
            <h2 class="display-1 font-weight-light mb-4">Reserva tu experiencia</h2>
            <p class="subtitle-1 grey--text">
              Completa el formulario y te contactaremos para confirmar tu cita
            </p>

            <v-form ref="form" v-model="valid" class="mt-6">
              <v-text-field
                v-model="reserva.nombre"
                label="Nombre completo"
                outlined
                :rules="[(v) => !!v || 'Campo requerido']"
                prepend-inner-icon="mdi-account"
              ></v-text-field>

              <v-text-field
                v-model="reserva.email"
                label="Correo electrónico"
                outlined
                type="email"
                :rules="[
                  (v) => !!v || 'Campo requerido',
                  (v) => /.+@.+\..+/.test(v) || 'Email inválido',
                ]"
                prepend-inner-icon="mdi-email"
              ></v-text-field>

              <v-text-field
                v-model="reserva.telefono"
                label="Teléfono"
                outlined
                :rules="[(v) => !!v || 'Campo requerido']"
                prepend-inner-icon="mdi-phone"
              ></v-text-field>

              <v-select
                v-model="reserva.servicio"
                :items="services.map((s) => s.name)"
                label="Selecciona un servicio"
                outlined
                :rules="[(v) => !!v || 'Selecciona un servicio']"
                prepend-inner-icon="mdi-spa"
              ></v-select>

              <v-text-field
                v-model="reserva.fecha"
                label="Fecha preferida"
                type="date"
                outlined
                :min="minDate"
                :rules="[(v) => !!v || 'Selecciona una fecha']"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>

              <v-select
                v-model="reserva.hora"
                :items="horas"
                label="Hora preferida"
                outlined
                :rules="[(v) => !!v || 'Selecciona una hora']"
                prepend-inner-icon="mdi-clock"
              ></v-select>

              <v-textarea
                v-model="reserva.comentarios"
                label="Comentarios adicionales"
                outlined
                rows="3"
                prepend-inner-icon="mdi-message"
              ></v-textarea>

              <v-btn
                color="#6B8E6B"
                dark
                x-large
                block
                rounded
                :disabled="!valid"
                @click="enviarReserva"
              >
                <v-icon left>mdi-calendar-check</v-icon>
                Confirmar Reserva
              </v-btn>
            </v-form>
          </v-col>

          <v-col cols="12" md="6">
            <v-img
              src="https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2"
              height="100%"
              class="rounded-lg"
            ></v-img>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <!-- Información de contacto -->
    <v-container class="mt-12">
      <v-row>
        <v-col cols="12" md="4">
          <v-card flat class="text-center pa-4">
            <v-icon size="48" color="#6B8E6B">mdi-map-marker</v-icon>
            <h3 class="title mt-3">Ubicación</h3>
            <p class="body-2 mt-2">
              Av. Los Jardines 123<br />
              San Isidro, Lima - Perú
            </p>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card flat class="text-center pa-4">
            <v-icon size="48" color="#6B8E6B">mdi-clock-outline</v-icon>
            <h3 class="title mt-3">Horario</h3>
            <p class="body-2 mt-2">
              Lunes a Viernes: 9am - 8pm<br />
              Sábados: 10am - 6pm<br />
              Domingos: Cerrado
            </p>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card flat class="text-center pa-4">
            <v-icon size="48" color="#6B8E6B">mdi-phone</v-icon>
            <h3 class="title mt-3">Contacto</h3>
            <p class="body-2 mt-2">
              📞 +51 928 180 748<br />
              ✉️ info@spazen.com<br />
              🌐 www.spazen.com
            </p>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Mapa -->
    <v-container class="mt-4">
      <v-card>
        <v-img
          src="https://maps.googleapis.com/maps/api/staticmap?center=Lima,Peru&zoom=12&size=1200x300&maptype=roadmap&markers=color:green%7CLima,Peru&key=YOUR_API_KEY"
          height="300"
        >
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular indeterminate color="#6B8E6B"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </v-card>
    </v-container>

    <!-- Newsletter -->
    <v-container class="mt-12 mb-12">
      <v-card color="#6B8E6B" dark class="pa-8 text-center">
        <v-card-title class="display-1 justify-center font-weight-light">
          Suscríbete a nuestro newsletter
        </v-card-title>
        <v-card-text class="subtitle-1">
          Recibe ofertas exclusivas y tips de bienestar
        </v-card-text>
        <v-form class="mt-4">
          <v-row justify="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="newsletterEmail"
                label="Tu correo electrónico"
                outlined
                dark
                solo
                append-outer-icon="mdi-email-send"
                @click:append-outer="suscribirNewsletter"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- Footer -->
    <v-footer color="grey darken-4" dark class="mt-12">
      <v-container>
        <v-row>
          <v-col cols="12" md="4">
            <v-icon size="32" class="mb-2">mdi-flower-lotus</v-icon>
            <h3 class="title">Spa Zen</h3>
            <p class="caption mt-2">Encuentra tu equilibrio interior</p>
          </v-col>

          <v-col cols="12" md="4">
            <h3>Enlaces rápidos</h3>
            <v-list dense dark>
              <v-list-item @click="scrollToTop">
                <v-list-item-icon><v-icon small>mdi-arrow-up</v-icon></v-list-item-icon>
                <v-list-item-content>Inicio</v-list-item-content>
              </v-list-item>
              <v-list-item @click="scrollToSection('reservas')">
                <v-list-item-icon><v-icon small>mdi-calendar</v-icon></v-list-item-icon>
                <v-list-item-content>Reservas</v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>

          <v-col cols="12" md="4">
            <h3>Síguenos</h3>
            <div class="mt-3">
              <v-btn icon dark class="mr-2">
                <v-icon>mdi-facebook</v-icon>
              </v-btn>
              <v-btn icon dark class="mr-2">
                <v-icon>mdi-instagram</v-icon>
              </v-btn>
              <v-btn icon dark>
                <v-icon>mdi-whatsapp</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <div class="text-center caption">
          © 2024 Spa Zen - Todos los derechos reservados. Un espacio para tu bienestar.
        </div>
      </v-container>
    </v-footer>

    <!-- Dialog de confirmación -->
    <v-dialog v-model="dialogConfirmacion" max-width="400">
      <v-card>
        <v-card-title class="headline success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          ¡Reserva exitosa!
        </v-card-title>
        <v-card-text>
          Gracias {{ reserva.nombre }}. Hemos recibido tu solicitud para el servicio de
          {{ reserva.servicio }} el día {{ reserva.fecha }} a las {{ reserva.hora }}.
          <br /><br />
          Te contactaremos a la brevedad para confirmar tu cita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="#6B8E6B" text @click="dialogConfirmacion = false"> Cerrar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Lightbox para galería -->
    <v-dialog v-model="galleryDialog" max-width="800">
      <v-card>
        <v-img :src="gallery[currentGalleryIndex]" height="500" contain>
          <v-btn absolute top right fab small dark @click="galleryDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-img>
        <v-card-actions>
          <v-btn :disabled="currentGalleryIndex === 0" @click="currentGalleryIndex--">
            <v-icon>mdi-chevron-left</v-icon> Anterior
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :disabled="currentGalleryIndex === gallery.length - 1"
            @click="currentGalleryIndex++"
          >
            Siguiente <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "SpaZen",
  data() {
    return {
      valid: false,
      dialogConfirmacion: false,
      galleryDialog: false,
      currentGalleryIndex: 0,
      newsletterEmail: "",
      services: [
        {
          name: "Masaje Relajante",
          description: "Masaje sueco que alivia tensiones y reduce el estrés",
          price: 120,
          rating: 4.9,
          reviews: 128,
          image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874",
          duration: "60 min",
        },
        {
          name: "Tratamiento Facial",
          description: "Limpieza profunda con productos orgánicos",
          price: 150,
          rating: 4.8,
          reviews: 95,
          image: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881",
          duration: "50 min",
        },
        {
          name: "Exfoliación Corporal",
          description: "Renueva tu piel con sales marinas y aceites esenciales",
          price: 130,
          rating: 4.7,
          reviews: 87,
          image: "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2",
          duration: "45 min",
        },
        {
          name: "Hidroterapia",
          description: "Baños de hidromasaje con aromaterapia",
          price: 180,
          rating: 4.9,
          reviews: 112,
          image: "https://images.unsplash.com/photo-1519823551278-64ac92734fb1",
          duration: "40 min",
        },
        {
          name: "Piedras Calientes",
          description: "Terapia con piedras volcánicas para relajación profunda",
          price: 200,
          rating: 4.9,
          reviews: 76,
          image: "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2",
          duration: "75 min",
        },
        {
          name: "Paquete Completo",
          description: "Día completo de spa con almuerzo incluido",
          price: 350,
          rating: 5.0,
          reviews: 45,
          image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874",
          duration: "4 horas",
        },
      ],
      gallery: [
        "https://images.unsplash.com/photo-1544161515-4ab6ce6db874",
        "https://images.unsplash.com/photo-1519823551278-64ac92734fb1",
        "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881",
        "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2",
        "https://images.unsplash.com/photo-1526738549149-8e07eca6c147",
        "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446",
      ],
      benefits: [
        {
          icon: "mdi-meditation",
          title: "Ambiente Relajante",
          description:
            "Espacios diseñados para tu tranquilidad con música y aromas terapéuticos",
          color: "#6B8E6B",
        },
        {
          icon: "mdi-leaf",
          title: "Productos Naturales",
          description: "Utilizamos productos orgánicos y libres de químicos dañinos",
          color: "#8FBC8F",
        },
        {
          icon: "mdi-account-heart",
          title: "Profesionales Calificados",
          description: "Terapeutas certificados con años de experiencia",
          color: "#6B8E6B",
        },
        {
          icon: "mdi-car",
          title: "Estacionamiento Gratuito",
          description: "Contamos con estacionamiento privado para nuestros clientes",
          color: "#8FBC8F",
        },
        {
          icon: "mdi-water",
          title: "Área de Hidroterapia",
          description: "Jacuzzi, sauna y baño turco incluidos",
          color: "#6B8E6B",
        },
        {
          icon: "mdi-cup",
          title: "Té de Bienvenida",
          description: "Te ofrecemos una selección de tés orgánicos",
          color: "#8FBC8F",
        },
      ],
      testimonials: [
        {
          name: "María González",
          text:
            "La mejor experiencia de spa que he tenido. El masaje relajante fue increíble, salí completamente renovada.",
          rating: 5,
          avatar: "https://randomuser.me/api/portraits/women/1.jpg",
        },
        {
          name: "Carlos Rodríguez",
          text:
            "Excelente atención y profesionales muy capacitados. El ambiente es muy tranquilo y relajante.",
          rating: 5,
          avatar: "https://randomuser.me/api/portraits/men/1.jpg",
        },
        {
          name: "Ana Martínez",
          text:
            "El tratamiento facial es maravilloso. Mi piel nunca había lucido tan bien. Volveré sin duda.",
          rating: 5,
          avatar: "https://randomuser.me/api/portraits/women/2.jpg",
        },
      ],
      horas: [
        "09:00",
        "10:00",
        "11:00",
        "12:00",
        "13:00",
        "14:00",
        "15:00",
        "16:00",
        "17:00",
        "18:00",
        "19:00",
      ],
      reserva: {
        nombre: "",
        email: "",
        telefono: "",
        servicio: "",
        fecha: "",
        hora: "",
        comentarios: "",
      },
    };
  },
  computed: {
    minDate() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    selectService(service) {
      this.reserva.servicio = service.name;
      this.scrollToSection("reservas");
    },
    openGallery(index) {
      this.currentGalleryIndex = index;
      this.galleryDialog = true;
    },
    enviarReserva() {
      if (this.$refs.form.validate()) {
        this.dialogConfirmacion = true;
        // Aquí puedes enviar los datos a tu API
        console.log("Reserva enviada:", this.reserva);

        // Resetear formulario después de enviar
        setTimeout(() => {
          this.reserva = {
            nombre: "",
            email: "",
            telefono: "",
            servicio: "",
            fecha: "",
            hora: "",
            comentarios: "",
          };
          this.$refs.form.reset();
        }, 2000);
      }
    },
    suscribirNewsletter() {
      if (this.newsletterEmail && /.+@.+\..+/.test(this.newsletterEmail)) {
        alert("¡Gracias por suscribirte! Recibirás nuestras ofertas exclusivas.");
        this.newsletterEmail = "";
      } else {
        alert("Por favor ingresa un email válido");
      }
    },
  },
};
</script>

<style scoped>
.spa-zen {
  background: linear-gradient(135deg, #f5f5f0 0%, #e8e8e0 100%);
  min-height: 100vh;
}

.hero-content {
  background: rgba(0, 0, 0, 0.4);
  padding: 40px;
  border-radius: 20px;
  backdrop-filter: blur(10px);
}

.service-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 15px;
  overflow: hidden;
}

.service-card-hover {
  transform: translateY(-10px);
}

.service-image {
  position: relative;
  overflow: hidden;
}

.service-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(107, 142, 107, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.gallery-img {
  cursor: pointer;
  transition: transform 0.3s ease;
  border-radius: 10px;
  overflow: hidden;
}

.gallery-img:hover {
  transform: scale(1.05);
}

.benefit-card {
  transition: transform 0.3s ease;
  padding: 20px;
}

.benefit-card:hover {
  transform: translateY(-5px);
}

.testimonial-card {
  transition: transform 0.3s ease;
  height: 100%;
}

.testimonial-card:hover {
  transform: translateY(-5px);
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.service-card,
.benefit-card,
.testimonial-card {
  animation: fadeIn 0.6s ease;
}

/* Responsive */
@media (max-width: 600px) {
  .display-3 {
    font-size: 2rem !important;
  }

  .display-1 {
    font-size: 1.8rem !important;
  }

  .hero-content {
    padding: 20px;
  }

  .service-card {
    margin-bottom: 16px;
  }
}

/* Scroll personalizado */
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #6b8e6b;
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: #5a7a5a;
}
</style>
